// Named constants shared by the Worker, the client, tests, and scripts.
// This is the one module where bare literals are allowed (see eslint config);
// everywhere else a tunable must be imported from here or a sibling
// `constants.ts`.

export const APP_NAME = 'Project Map';

export const HTTP_STATUS = {
  OK: 200,
  CREATED: 201,
  ACCEPTED: 202,
  NO_CONTENT: 204,
  /** The first of the 3xx statuses. */
  MULTIPLE_CHOICES: 300,
  FOUND: 302,
  NOT_MODIFIED: 304,
  BAD_REQUEST: 400,
  UNAUTHORIZED: 401,
  FORBIDDEN: 403,
  NOT_FOUND: 404,
  CONFLICT: 409,
  PRECONDITION_FAILED: 412,
  PAYLOAD_TOO_LARGE: 413,
  UNSUPPORTED_MEDIA_TYPE: 415,
  TOO_MANY_REQUESTS: 429,
  INTERNAL_SERVER_ERROR: 500,
  BAD_GATEWAY: 502,
  SERVICE_UNAVAILABLE: 503,
} as const;

/** Machine-readable error codes returned as `{ error: <code> }`. */
export const API_ERROR = {
  /** Signed in, but the account must pick its own password before anything else. */
  PASSWORD_CHANGE_REQUIRED: 'password_change_required',
  /** A second-factor code or recovery code that does not match. */
  INVALID_CODE: 'invalid_code',
  /** The code step of a sign-in without a live challenge (none, or expired). */
  CHALLENGE_EXPIRED: 'challenge_expired',
  NOT_FOUND: 'not_found',
  VALIDATION: 'validation_failed',
  UNAUTHENTICATED: 'unauthenticated',
  INVALID_CREDENTIALS: 'invalid_credentials',
  CSRF: 'cross_site_request_rejected',
  RATE_LIMITED: 'too_many_login_attempts',
  FORBIDDEN: 'forbidden',
  MAIL_UNAVAILABLE: 'mail_unavailable',
  CONFLICT: 'conflict',
  /** An `If-Match` naming a version the row no longer has: nothing was written. */
  STALE: 'stale',
  /** A create carrying an `id` that already names a different row. */
  ID_TAKEN: 'id_taken',
  PAYLOAD_TOO_LARGE: 'payload_too_large',
  UNSUPPORTED_MEDIA_TYPE: 'unsupported_media_type',
  QUOTA_EXCEEDED: 'quota_exceeded',
  PDF_UNAVAILABLE: 'pdf_unavailable',
  ADDRESS_LOOKUP_UNAVAILABLE: 'address_lookup_unavailable',
  CREDENTIALS_UNAVAILABLE: 'credentials_unavailable',
  /** A bearer token that is unknown, revoked, or expired. */
  TOKEN_INVALID: 'invalid_token',
  /** A read-only token on a route that writes. */
  TOKEN_SCOPE: 'token_scope',
  /** A route that never takes a token (sign-in, sessions, secrets, tokens themselves). */
  TOKEN_NOT_ALLOWED: 'token_not_allowed',
  /** A request carrying both a session cookie and a bearer token. */
  AMBIGUOUS_CREDENTIALS: 'ambiguous_credentials',
  INTERNAL: 'internal_error',
  MISCONFIGURED: 'server_misconfigured',
} as const;

export type ApiErrorCode = (typeof API_ERROR)[keyof typeof API_ERROR];

// --- Authentication -------------------------------------------------------

// PBKDF2-SHA256 work factor for the login password hash. Cloudflare Workers
// hard-cap PBKDF2 at 100,000 iterations (DOMException above that), so this
// is the platform maximum rather than the OWASP recommendation of 600,000,
// whatever the plan's CPU budget (10 ms per request on Free, 30 s on Paid).
// The generated password's entropy (see PASSWORD_LENGTH) is the primary
// defence; this iteration count is defence in depth. M1 certification
// measured the real login CPU time in production logs (PLAN.md §5).
export const AUTH_PBKDF2_ITERATIONS = 100_000;
export const AUTH_PBKDF2_SALT_BYTES = 16;
export const AUTH_PBKDF2_HASH_BITS = 256;
export const AUTH_PASSWORD_HASH_PREFIX = 'pbkdf2-sha256';

// 22 characters from a 62-symbol alphabet ≈ 131 bits of entropy.
export const PASSWORD_LENGTH = 22;
export const PASSWORD_ALPHABET = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789';

// The session-signing key and the credential key are both 256-bit.
export const SECRET_KEY_BYTES = 32;

// --- Device credentials (AES-256-GCM, worker/crypto/aead.ts) ---------------

/** GCM's standard nonce length; a fresh random one per sealed value. */
export const CREDENTIAL_IV_BYTES = 12;
/** A password or passphrase as typed. */
export const SECRET_MAX_LENGTH = 1000;
/** A private key in PEM is a few kilobytes; this leaves room for a note beside it. */
export const SSH_KEY_MAX_LENGTH = 8192;
export const CREDENTIAL_NOTES_MAX_LENGTH = 2000;
export const SSH_PORT_MIN = 1;
export const SSH_PORT_MAX = 65_535;
/** Revealed credentials leave the screen on their own after this long. */
export const CREDENTIALS_HIDE_AFTER_MS = 60 * 1000;

// --- Client credential vault (ROADMAP K2) ---------------------------------

/** An entry's label ("Spectrum business portal"); matches the SQL CHECK. */
export const VAULT_LABEL_MAX_LENGTH = 120;
/** A base32 one-time-code seed as an authenticator app gives it, with room to spare. */
export const VAULT_TOTP_SEED_MAX_LENGTH = 256;

export const USERNAME_MAX_LENGTH = 64;
/** A chosen password (generated ones are PASSWORD_LENGTH); NIST's floor is 8, this is stricter. */
export const PASSWORD_MIN_LENGTH = 12;
export const PASSWORD_MAX_LENGTH = 256;

// --- Password reset by email ----------------------------------------------

export const RESET_TOKEN_BYTES = 32;
export const RESET_TOKEN_TTL_MINUTES = 60;
export const RESET_TOKEN_TTL_SECONDS = RESET_TOKEN_TTL_MINUTES * 60;

// --- Sessions -------------------------------------------------------------

export const SESSION_COOKIE_NAME = 'pm_session';
/** Absolute lifetime of a session: 30 days from sign-in, however active. */
export const SESSION_MAX_AGE_SECONDS = 30 * 24 * 60 * 60;
/** A session not used for this long ends: 7 days (a field tool, not a bank). */
export const SESSION_IDLE_TIMEOUT_SECONDS = 7 * 24 * 60 * 60;
/** How often a busy session's last-seen time is written back, at most. */
export const SESSION_TOUCH_INTERVAL_SECONDS = 5 * 60;
/** The User-Agent kept on a session row, for the session list. */
export const SESSION_USER_AGENT_MAX_LENGTH = 256;
/** What a request without a Cloudflare-supplied address is recorded as (local dev, tests). */
export const UNKNOWN_ADDRESS = 'unknown';

// --- Personal API tokens (migration 0033, worker/auth/api-token.ts) --------

/** Every token starts with this, so one is recognisable in a script or a leak scanner. */
export const API_TOKEN_PREFIX = 'pm_';
/** Random bytes behind a token (base64url after the prefix): 256 bits. */
export const API_TOKEN_BYTES = 32;
/** How much of a token its list shows: enough to tell two apart, useless to guess from. */
export const API_TOKEN_DISPLAY_LENGTH = 8;
export const API_TOKEN_NAME_MAX_LENGTH = 80;
/** What a token may do: read the map, or read and write it. */
export const API_TOKEN_SCOPES = ['read', 'write'] as const;
/** A token that expires does so within a year; one that never expires is allowed but is the person's choice. */
export const API_TOKEN_MAX_EXPIRY_DAYS = 365;
/** Live (unrevoked, unexpired) tokens one account may hold at once. */
export const API_TOKENS_PER_ACCOUNT = 10;
/** How often a busy token's last-used time is written back, at most. */
export const API_TOKEN_TOUCH_INTERVAL_SECONDS = 5 * 60;
/** Refused bearer tokens from one address in the login window before it is locked. */
export const TOKEN_MAX_FAILURES_PER_IP = 20;
/** The scheme the Authorization header carries a token under. */
export const BEARER_SCHEME = 'Bearer';

// --- Webhooks (migration 0033, worker/webhooks/) ---------------------------

/** Where a webhook may point: an https URL of at most this many characters. */
export const WEBHOOK_URL_MAX_LENGTH = 500;
/** What a webhook may subscribe to: map changes, and the security audit log. */
export const WEBHOOK_EVENTS = ['change', 'audit'] as const;
/** The signing secret: 256 random bits, base64url. */
export const WEBHOOK_SECRET_BYTES = 32;
/** How long one delivery waits for the receiver to answer. */
export const WEBHOOK_DELIVERY_TIMEOUT_MS = 10 * 1000;
/**
 * After a failed attempt n (counted from 1), the next is due this many
 * minutes later; once the list runs out the delivery is given up. Six
 * attempts over about twelve and a half hours.
 */
export const WEBHOOK_RETRY_MINUTES = [1, 5, 30, 120, 600] as const;
/** Failed attempts in a row (across deliveries) before a webhook is turned off by itself. */
export const WEBHOOK_DISABLE_AFTER_FAILURES = 20;
/** Deliveries listed per webhook, newest first. */
export const WEBHOOK_DELIVERIES_LISTED = 50;
/** Due deliveries re-attempted per scheduled run. */
export const WEBHOOK_RETRY_BATCH = 20;
/** A receiver's error, kept on the delivery row, is cut here. */
export const WEBHOOK_ERROR_MAX_LENGTH = 200;
/** The headers every delivery carries beside `Content-Type: application/json`. */
export const WEBHOOK_HEADERS = {
  EVENT: 'X-ProjectMap-Event',
  DELIVERY: 'X-ProjectMap-Delivery',
  TIMESTAMP: 'X-ProjectMap-Timestamp',
  SIGNATURE: 'X-ProjectMap-Signature',
} as const;
/** The signature header's value is this, then the hex HMAC-SHA256 of `timestamp.body`. */
export const WEBHOOK_SIGNATURE_PREFIX = 'sha256=';
/** The event a test delivery carries. */
export const WEBHOOK_PING_EVENT = 'ping';

// --- Profiles -------------------------------------------------------------

export const DISPLAY_NAME_MAX_LENGTH = 100;
export const PROFILE_TITLE_MAX_LENGTH = 100;
export const PHONE_MAX_LENGTH = 40;
/** A profile picture after the browser has resized it: a small square. */
export const AVATAR_EDGE_PX = 256;
export const AVATAR_MAX_BYTES = 2 * 1024 * 1024;

// --- Second factor (TOTP; worker/auth/totp-constants.ts has the RFC values) ---

/** The challenge cookie set after a correct password when a code is still owed. */
export const MFA_COOKIE_NAME = 'pm_mfa';
/** How long the code step may take after the password: 5 minutes. */
export const MFA_CHALLENGE_TTL_SECONDS = 5 * 60;
/** A typed code: six digits, or a recovery code with its hyphen and stray spaces. */
export const MFA_CODE_MAX_LENGTH = 16;

// --- Login rate limiting (D1-backed, see PLAN.md A6) ----------------------

export const LOGIN_RATE_LIMIT_WINDOW_SECONDS = 15 * 60;
/** Failed sign-ins against one account in the window before it is locked. */
export const LOGIN_MAX_FAILURES_PER_ACCOUNT = 5;
/** Failed sign-ins from one address in the window before it is locked. */
export const LOGIN_MAX_FAILURES_PER_IP = 10;
/** Attempts older than this are pruned on every login. */
export const LOGIN_ATTEMPT_RETENTION_SECONDS = 24 * 60 * 60;
/** Reset mails: the window, and how many per account and per address in it. */
export const RESET_REQUEST_WINDOW_SECONDS = 60 * 60;
export const RESET_REQUESTS_PER_ACCOUNT = 3;
export const RESET_REQUESTS_PER_IP = 10;
/** Wrong reset tokens from one address in the login window before it is locked. */
export const RESET_MAX_FAILURES_PER_IP = 10;

// --- Per-account quotas on costly routes (worker/http/quota.ts) -----------

export const QUOTA_WINDOW_SECONDS = 60 * 60;
/** PDF renders an hour: each spends Browser Rendering time, which the plan bills past what it includes. */
export const PDF_RENDERS_PER_HOUR = 20;
/** Photo uploads an hour: a busy site visit, not a script. */
export const PHOTO_UPLOADS_PER_HOUR = 300;
/** File uploads an hour: a day's tester results and manuals, not a bulk import. */
export const ATTACHMENT_UPLOADS_PER_HOUR = 120;
/** Address lookups an hour: typing in the property form, debounced. */
export const GEOCODE_LOOKUPS_PER_HOUR = 120;
/** Credential-key rotations an hour: each re-seals every sealed row. */
export const KEY_ROTATIONS_PER_HOUR = 5;
/** Device imports an hour: each can add up to IMPORT_MAX_ROWS devices, one write each. */
export const IMPORTS_PER_HOUR = 20;
/**
 * Multipart bodies (photo, file, and memo uploads) over this are refused
 * before they are read. It is the outermost ceiling only: every upload route
 * checks its own, tighter limit first — a photo against PHOTO_MAX_BYTES, a
 * file against ATTACHMENT_MAX_BYTES, a memo against VIDEO_MAX_BYTES or
 * AUDIO_MAX_BYTES — so raising this never widens what any route accepts. It
 * sits just above the largest of them (a video memo plus its framing).
 */
export const FORM_BODY_MAX_BYTES = 28 * 1024 * 1024;

// --- Password policy (shared/password-policy.ts) --------------------------

/** A password must not contain the username once the username is this long. */
export const PASSWORD_USERNAME_MIN_LENGTH = 3;
export const PASSWORD_MIN_DISTINCT_CHARACTERS = 4;
/** Substrings that put a password on every guessing list, whatever its length. */
export const COMMON_PASSWORD_FRAGMENTS = [
  'password',
  'passw0rd',
  'qwerty',
  'letmein',
  'welcome',
  'iloveyou',
  '12345678',
] as const;

// --- Request bodies -------------------------------------------------------

/**
 * Ceiling for a JSON request body. The largest legitimate one — a switch
 * with 128 labelled ports, 8 WAN links, and full notes, or a credential
 * record with an 8 KB key — is under 64 KB; anything past this is not a
 * form and is refused before it is parsed.
 */
export const JSON_BODY_MAX_BYTES = 256 * 1024;

// --- Entity field limits --------------------------------------------------

export const NAME_MAX_LENGTH = 200;
export const SHORT_TEXT_MAX_LENGTH = 500;
export const NOTES_MAX_LENGTH = 5000;
export const EXTRAS_MAX_ENTRIES = 50;
export const EXTRA_KEY_MAX_LENGTH = 100;
export const EXTRA_VALUE_MAX_LENGTH = 1000;
export const PORTS_MAX_ENTRIES = 128;
/** Port counts switches actually come in; the form offers only these. */
export const SWITCH_PORT_COUNTS = [4, 5, 8, 10, 12, 16, 24, 28, 48, 52] as const;
/** LAN ports a gateway (modem, router, firewall) commonly has, offered as one-tap picks. */
export const GATEWAY_PORT_PRESETS = [1, 2, 4, 5, 8, 10, 16] as const;
export const GATEWAY_MAX_PORTS = 64;
/** Jacks a telecom wall outlet usually has, offered as one-tap picks (ROADMAP D6). */
export const OUTLET_JACK_PRESETS = [1, 2, 4, 6] as const;
/** No wall plate holds more jacks than this. */
export const OUTLET_MAX_JACKS = 12;
/** Patch panels are counted with a stepper; nothing sensible exceeds this. */
/** The carriers a modem or ONT is usually from; "Other" reveals a name field. */
export const ISP_NAMES = [
  'AT&T',
  'Verizon Fios',
  'Spectrum',
  'Xfinity',
  'Cox',
  'Starlink',
  'T-Mobile Home Internet',
  'Frontier',
  'Quantum Fiber (CenturyLink)',
  'Optimum',
  'Google Fiber',
  'Other',
] as const;
export const OTHER_CHOICE = 'Other';
/** What a server or NAS does; a box can hold several at once. */
export const SERVER_ROLES = [
  'File share',
  'DHCP server',
  'DNS server',
  'Email server',
  'Domain controller',
  'Backup target',
  'Virtualization host',
  'Web server',
  'Database',
  'Camera recording',
  'Print server',
  'Other',
] as const;
export const PATCH_PANEL_MAX_PORTS = 999;
// A media converter or passive splitter (ROADMAP J5) mates two connector
// sides: port 1 is the A side (the input), every higher port is a B side leg.
// Two ports is the plain converter; a 1:32 optical splitter is the widest
// anyone hangs on a wall, so 33 ports is the ceiling.
export const CONVERTER_MIN_PORTS = 2;
export const CONVERTER_MAX_PORTS = 33;
/** Port counts offered as one-tap picks: a pair, then 1:2, 1:4, 1:8, 1:16. */
export const CONVERTER_PORT_PRESETS = [2, 3, 5, 9, 17];
/** Metres in one foot, for comparing a run's length against a cable type's recommended maximum (ROADMAP A13). */
export const METERS_PER_FOOT = 0.3048;
/** A LAG or mirror group's label ("ag0", "Uplink bond", "Mirror to IDS"); ROADMAP C5. */
export const PORT_GROUP_LABEL_MAX_LENGTH = 40;
/** The most ports one LAG or mirror group may name; a bond or a mirror wider than this is not one anyone documents. */
export const PORT_GROUP_MAX_MEMBERS = 64;
/** How a WAN gets its address. */
export const WAN_ADDRESS_MODES = ['DHCP', 'Static', 'PPPoE'] as const;
/** WAN links a gateway can carry; more than this is not a gateway anyone documents. */
export const WANS_MAX_ENTRIES = 8;
/** A VLAN tag is 1–4094 (802.1Q); untagged is left blank rather than written 0. */
export const VLAN_TAG_MIN = 1;
export const VLAN_TAG_MAX = 4094;
/** A VLAN's name on the property's table ("Cameras", "Guest Wi-Fi"). */
export const VLAN_NAME_MAX_LENGTH = 60;
/** What shape a computer comes in. */
export const COMPUTER_FORM_FACTORS = [
  'Desktop',
  'Laptop',
  'Workstation',
  'Mini PC',
  'Thin client',
  'Tablet',
  'Other',
] as const;
/** What kind of printer sits on the network. */
export const PRINTER_KINDS = [
  'Laser',
  'Inkjet',
  'Multifunction',
  'Label',
  'Receipt',
  'Plotter',
  'Other',
] as const;
/** How an endpoint reaches the network. */
export const ENDPOINT_CONNECTIONS = ['Wired', 'Wi-Fi', 'Wired and Wi-Fi'] as const;
// A device's service history (ROADMAP J4): one line saying what happened, the
// notes beneath it, and whoever did the work.
export const SERVICE_SUMMARY_MAX_LENGTH = 200;
export const SERVICE_NOTES_MAX_LENGTH = 2000;
export const SERVICE_TECHNICIAN_MAX_LENGTH = 120;

// A stock place (ROADMAP J2): the warehouse, a van, an office shelf. Its name
// is a node name (NAME_MAX_LENGTH); what is written about it — the shelf
// numbering, the registration of the van — is bounded like every other
// record's notes.
export const STOCK_PLACE_NOTES_MAX_LENGTH = 2000;

// Security and life-safety devices (ROADMAP J7). A reader, a door's hardware,
// and a notification appliance each come in a handful of kinds an installer
// names on the drawing; the ceilings are the largest panel anyone fits.
/** What a credential is presented to at the door. */
export const ACCESS_READER_KINDS = [
  'Card',
  'Fob',
  'Keypad',
  'Card and keypad',
  'Biometric',
  'Mobile credential',
  'Multi-technology',
  'Other',
] as const;
/** The hardware that holds a door shut, and what it does when power is lost. */
export const DOOR_HARDWARE_KINDS = [
  'Electric strike',
  'Maglock',
  'Electrified lever',
  'Electrified mortise',
  'Electrified panic bar',
  'Other',
] as const;
export const DOOR_FAIL_MODES = ['Fail secure', 'Fail safe'] as const;
/** What a notification appliance does when the panel calls for it. */
export const NOTIFICATION_KINDS = [
  'Horn',
  'Strobe',
  'Horn/strobe',
  'Speaker/strobe',
  'Bell',
  'Other',
] as const;
/** Doors one access control panel drives, boards included. */
export const ACCESS_PANEL_MAX_DOORS = 128;
/** Zones one intrusion or fire panel carries; an addressable loop is the widest. */
export const PANEL_MAX_ZONES = 1024;

// AV and control devices (ROADMAP J6). Each ceiling is the largest figure any
// installed unit of that kind carries, so a typo is caught without refusing a
// real device.
/** Amplifier channels; a large distributed-audio amplifier has this many zones at most. */
export const AMPLIFIER_MAX_CHANNELS = 64;
/** What one amplifier channel or one speaker is rated at, in watts. */
export const AUDIO_MAX_WATTS = 5000;
/** A projector's brightness in ANSI lumens. */
export const PROJECTOR_MAX_LUMENS = 50_000;
/** Buttons on one keypad. */
export const KEYPAD_MAX_BUTTONS = 24;
/** What a sensor senses (ROADMAP J6 and J7: the same device serves AV, automation, and alarm). */
export const SENSOR_KINDS = [
  'Motion',
  'Occupancy',
  'Door / window contact',
  'Glass break',
  'Smoke',
  'Heat',
  'Carbon monoxide',
  'Water',
  'Temperature',
  'Humidity',
  'Daylight',
  'Other',
] as const;
/** How a screen is held up (ROADMAP J8); a signage panel and a meeting-room display differ by this. */
export const SCREEN_MOUNTS = ['Wall', 'Stand', 'Ceiling', 'Cart', 'Recessed', 'Other'] as const;
/** How a 3D printer lays material down (ROADMAP J8). */
export const PRINTER_3D_PROCESSES = ['FDM', 'SLA', 'MSLA', 'SLS', 'Other'] as const;
/** A screen's diagonal in inches; nothing sold as one display is larger than this. */
export const SCREEN_DIAGONAL_MAX_INCHES = 200;
/** A switch's PoE budget in watts; nothing a closet holds supplies more than this. */
export const SWITCH_POE_BUDGET_MAX_WATTS = 10_000;
/** What one port can draw in watts (802.3bt type 4 is 90 W at the PSE). */
export const PORT_POE_MAX_WATTS = 100;
/** The bands a Wi-Fi radio works in. */
export const RADIO_BANDS = ['2.4 GHz', '5 GHz', '6 GHz'] as const;
/** Channel widths in MHz a radio can be set to; blank when not recorded. */
export const RADIO_WIDTHS = ['20', '40', '80', '160', '320'] as const;
/** Radios one device carries; a tri-band AP has three, a few have four. */
export const RADIOS_MAX_ENTRIES = 6;
/**
 * How high an access point hangs, in whole feet or metres (ROADMAP J3). A
 * survey tool records the height because coverage depends on it; nothing
 * anyone mounts by hand goes above this, in either unit.
 */
export const MOUNT_HEIGHT_MAX = 200;
/** The antenna an access point uses; what a survey (Ekahau, Hamina) asks for first. */
export const ANTENNA_TYPES = [
  'Internal',
  'Omni',
  'Directional',
  'Patch',
  'Sector',
  'Yagi',
  'Other',
] as const;
/** Which way the antenna faces, as a survey records it. */
export const ANTENNA_ORIENTATIONS = [
  'Down (ceiling)',
  'Up (floor)',
  'Horizontal (wall)',
  'Angled',
] as const;

// --- Port fields: connector, media/speed, VLAN mode, PoE mode ------------
// These extend a port row (shared/item-types.ts `portSchema`), which lives
// in a device's `details` JSON, so no migration carries them: a port
// recorded before any of them simply leaves it blank/absent, and the reads
// below treat that as the default. B2 (connector), C2 (speed, duplex, and
// enabled), C3 (VLAN mode and tagged tags), and C4 (PoE mode) all live here.

/** The physical connector a port presents; blank/absent for a port recorded before B2. */
export const PORT_CONNECTORS = [
  'RJ45',
  'SFP',
  'SFP+',
  'SFP28',
  'QSFP',
  'LC',
  'SC',
  'E2000',
  'ST',
  'MPO',
  'console',
  'USB',
  'power',
] as const;
/** The link speed a port runs at (negotiated or configured); blank when not recorded. */
export const PORT_SPEEDS = ['100M', '1G', '2.5G', '5G', '10G', '25G', '40G', '100G'] as const;
/** The duplex a port runs at (C2): negotiated, or forced full or half; blank when not recorded. */
export const PORT_DUPLEXES = ['auto', 'full', 'half'] as const;
/** How a port carries VLANs: one access tag, or a trunk with a native tag and tagged tags. */
export const PORT_MODES = ['access', 'trunk'] as const;
export const DEFAULT_PORT_MODE = 'access';
/**
 * PoE the port supplies: off, auto (negotiated), or a forced class. A port
 * recorded before C4 with the old `poe:true` reads as `auto`, `poe:false` as
 * `off`, so the boolean stays meaningful (shared/item-types.ts `portPoeMode`).
 */
export const PORT_POE_MODES = ['off', 'auto', 'af', 'at', 'bt'] as const;

// --- Modular panels, breakout positions, and numbering (ROADMAP I2/I3/I4) --
// A patch panel can hold swappable cassettes, each a small block of ports
// (I3); a rear port can carry several positions for a breakout, an MPO
// trunk fanning to N legs (I2); and a panel's ports can be numbered and laid
// out to taste (I4). All three live in a panel's `details` JSON beside its
// existing port model, so no port row or endpoint recorded before them reads
// any differently — a cassette-less panel is a flat port count, as it was.

/** Cassettes one modular panel may hold; more than this is not a panel anyone racks. */
export const PANEL_MAX_CASSETTES = 24;
/** Ports one cassette (module) carries; a high-density LC cassette tops out here. */
export const CASSETTE_MAX_PORTS = 48;
/** Ports a cassette usually comes in, offered as one-tap picks. */
export const CASSETTE_PORT_PRESETS = [6, 8, 12, 24] as const;
/** The ports a new cassette starts with (a common 12-fiber LC module). */
export const DEFAULT_CASSETTE_PORTS = 12;
/**
 * Positions a single multi-position port may fan out to (ROADMAP I2): an MPO
 * trunk breaks out to at most 24 legs (MPO-24). A port left at one position is
 * a plain single-jack port, exactly as before positions existed.
 */
export const PORT_MAX_POSITIONS = 24;
/** Position counts a breakout port usually has, offered as one-tap picks. */
export const PORT_POSITION_PRESETS = [4, 8, 12, 24] as const;
/** The largest number a panel's numbering may start from or step by (ROADMAP I4). */
export const PANEL_NUMBER_MAX = 9999;
/** A panel numbers its ports from 1, one at a time, unless its layout says otherwise. */
export const DEFAULT_PANEL_NUMBER_START = 1;
export const DEFAULT_PANEL_NUMBER_STEP = 1;
export const DEFAULT_PORT_POE_MODE = 'off';
export const PORT_POE_MODE_LABELS: Readonly<Record<(typeof PORT_POE_MODES)[number], string>> = {
  off: 'Off',
  auto: 'Auto',
  af: '802.3af',
  at: '802.3at',
  bt: '802.3bt',
};

// --- Port naming rules, opt-in per template (C9) --------------------------
/** A port name under an enforcing template: unique within the device, uppercase, at most this long. */
export const PORT_NAME_MAX_LENGTH = 10;
/** How much of an enforced port name is shown before an ellipsis takes the rest. */
export const PORT_NAME_DISPLAY_LENGTH = 5;

// --- Device templates with a face layout (B1) -----------------------------
// A preset may carry a `layout`: the device's front and back faces, each a
// set of port groups (a connector, a name prefix, a counting direction, and
// a grid of rows × columns whose port numbers are computed), text fields,
// and icons, at a height of 1–8U. The rack renderer (ROADMAP A1, a later
// wave) draws from it; here it is the data and its editor.
export const TEMPLATE_MIN_U = 1;
export const TEMPLATE_MAX_U = 8;
export const TEMPLATE_GROUP_MIN = 1;
export const TEMPLATE_GROUP_MAX_ROWS = 99;
export const TEMPLATE_GROUP_MAX_COLS = 99;
/** Port groups, text fields, and icons one face may carry. */
export const TEMPLATE_MAX_GROUPS = 24;
export const TEMPLATE_MAX_TEXTS = 24;
export const TEMPLATE_MAX_ICONS = 12;
export const TEMPLATE_PREFIX_MAX_LENGTH = 12;
export const TEMPLATE_TEXT_MAX_LENGTH = 40;
/** A port group's start number, and the ceiling a computed number may reach. */
export const TEMPLATE_PORT_NUMBER_MAX = 9999;
/** How a group's grid is counted: left-to-right (row by row) or top-to-bottom (column by column). */
export const TEMPLATE_COUNT_DIRECTIONS = ['ltr', 'ttb'] as const;
export const DEFAULT_COUNT_DIRECTION = 'ltr';

// --- Rack item faces (A2) -------------------------------------------------
/** Which face of the rack a device sits on; a full-depth device occupies both. */
export const ITEM_FACES = ['front', 'back'] as const;
export const DEFAULT_ITEM_FACE = 'front';

// --- Power feeds (shared/power.ts) ----------------------------------------

/**
 * The highest outlet number a feed may name. A source that declares how
 * many outlets it has is held to that count; one that leaves the field
 * blank is held to this ceiling, which is also the CHECK in migration
 * 0035. Nothing a technician documents has more outlets than this.
 */
export const OUTLET_MAX = 96;

// --- Search ---------------------------------------------------------------

export const SEARCH_QUERY_MIN_LENGTH = 2;
export const SEARCH_QUERY_MAX_LENGTH = 80;
/** Hits returned at most; the reply says when more matched. */
export const SEARCH_RESULT_LIMIT = 30;

// --- Address autocomplete -------------------------------------------------

// Address suggestions come from Mapbox Geocoding v6, proxied through the
// Worker so the token stays a server secret (worker/geocode/mapbox.ts).
// Without a MAPBOX_TOKEN the feature is off and the field is a plain input.
export const ADDRESS_QUERY_MIN_LENGTH = 3;
export const ADDRESS_QUERY_MAX_LENGTH = 200;
/** Suggestions offered at once; a phone list longer than this is noise. */
export const ADDRESS_SUGGESTION_LIMIT = 5;
/** Milliseconds a keystroke waits before a lookup, so typing is not one call per letter. */
export const ADDRESS_SUGGEST_DEBOUNCE_MS = 250;
/** Mapbox forward-geocoding endpoint; the token is added by the Worker. */
export const MAPBOX_GEOCODE_URL = 'https://api.mapbox.com/search/geocode/v6/forward';
/** Mapbox Static Images: the map picture on a property page, proxied by the Worker. */
export const MAPBOX_STATIC_URL = 'https://api.mapbox.com/styles/v1/mapbox/streets-v12/static';
export const MAP_IMAGE_WIDTH_PX = 640;
export const MAP_IMAGE_HEIGHT_PX = 360;
/** Street level with the block around the building. */
export const MAP_ZOOM = 15;
/** What Mapbox's terms require beside a static map. */
export const MAP_ATTRIBUTION = '© Mapbox © OpenStreetMap';
/** How long a save waits for the address to be placed before saving it unplaced. */
export const GEOCODE_TIMEOUT_MS = 4000;
/** How long after a lookup that found nothing a property read waits before trying again. */
export const PLACE_RETRY_SECONDS = 60 * 60;

/** How far back the dashboard's activity chart looks, in days. */
export const ACTIVITY_DAYS = 30;
/**
 * How far back the API counts changes: a day more than the chart shows,
 * since the chart's days are the browser's and the first of them can start
 * before the API's UTC cut-off.
 */
export const ACTIVITY_WINDOW_SECONDS = (ACTIVITY_DAYS + 1) * 24 * 60 * 60;
/** How many nodes each "Needs attention" list names before it says how many more there are. */
export const ATTENTION_ROW_LIMIT = 100;
/** A warranty ending within this many days (or already ended) is worth a word before a visit. */
export const ATTENTION_WARRANTY_DAYS = 90;
/** An expiring record (an SSL certificate, a domain, a licence) within this many days is worth a word. */
export const ATTENTION_RECORD_DAYS = 90;
/** How many device types the dashboard's wheel names before it gathers the rest. */
export const OVERVIEW_WHEEL_SLICES = 6;
/** How many clients the dashboard's "devices per client" chart ranks. */
export const OVERVIEW_TOP_CLIENTS = 8;
/** Map pictures an hour per account; each is a Mapbox request. */
export const MAP_IMAGES_PER_HOUR = 300;
/** A map picture of an address is good for a day in the browser. */
export const MAP_IMAGE_MAX_AGE_SECONDS = 24 * 60 * 60;
/** The map of every property (GET /api/map): wider than a property's, since it fits them all. */
export const MAP_OVERVIEW_WIDTH_PX = 1024;
export const MAP_OVERVIEW_HEIGHT_PX = 576;
/** Pixels between the outermost pins and the picture's edge when the view fits them all. */
export const MAP_OVERVIEW_PADDING_PX = 48;
/**
 * Pins the overview draws at most: a Mapbox Static Images URL is capped at
 * 8 KB and each pin costs about 30 characters. The page says when the map
 * shows fewer properties than are placed.
 */
export const MAP_OVERVIEW_MAX_PINS = 200;

// --- Runs from a pattern (POST /api/rooms/:id/drops/batch) ----------------

/** The counter in a label pattern: "{n}" becomes 1, 2, 3, … from `start`. */
export const DROP_BATCH_COUNTER = '{n}';
/** The port in a label pattern: "{port}" becomes the panel port each run lands on. */
export const DROP_BATCH_PORT = '{port}';
/** Runs one batch makes at most: two 48-port panels' worth. */
export const DROP_BATCH_MAX = 96;
/** Digits the counter is padded to at most ("001"). */
export const DROP_BATCH_PADDING_MAX = 3;

// --- Cable IDs, tester readings, and pathways (ROADMAP I7, I9, I10) -------

/** A cable ID (ROADMAP I7): bounded so it fits a label and a schedule cell. */
export const CABLE_ID_MAX_LENGTH = 40;
/** The prefix a generated cable ID carries: "CBL-0001". */
export const CABLE_ID_PREFIX = 'CBL';
/** Digits a generated cable ID's number is padded to. */
export const CABLE_ID_PAD = 4;
/** The tester's make/model/serial kept on a reading (ROADMAP I9). */
export const READING_TESTER_MAX_LENGTH = 120;
/** A reading's free-text note. */
export const READING_NOTES_MAX_LENGTH = 500;
/** A pathway's name (ROADMAP I10). */
export const PATHWAY_NAME_MAX_LENGTH = 200;
/** A pathway's trade-size text ("1 in EMT"). */
export const PATHWAY_TRADE_SIZE_MAX_LENGTH = 60;
/** A pathway's free-text note. */
export const PATHWAY_NOTES_MAX_LENGTH = 500;
/** The most cables a pathway's capacity may be set to. */
export const PATHWAY_CAPACITY_MAX = 10_000;

// --- Node identifiers and the naming scheme (ROADMAP B5, B6, E1) ----------

/** A node's short path segment (B6): bounded so it fits a label and a table cell. */
export const IDENTIFIER_MAX_LENGTH = 40;
/** A per-type ID prefix in the naming scheme ("SW", "PP"); short and uppercase (B5). */
export const IDENTIFIER_PREFIX_MAX_LENGTH = 8;
/** What sits between a prefix and its number ("" → SW01, "-" → SW-01). */
export const IDENTIFIER_SEPARATOR_MAX_LENGTH = 4;
/** The separator a fresh scheme uses: none, so a generated ID reads "SW01". */
export const IDENTIFIER_SEPARATOR_DEFAULT = '';
/** Digits a generated identifier's number is padded to by default ("01"). */
export const IDENTIFIER_PAD_DEFAULT = 2;
/** The narrowest and widest the padding may be set to (1 → "1", 6 → "000001"). */
export const IDENTIFIER_PAD_MIN = 1;
export const IDENTIFIER_PAD_MAX = 6;

// --- Change history -------------------------------------------------------

/** Changes listed per page; the reply says when older ones exist. */
export const HISTORY_PAGE_SIZE = 50;
/** Audit entries listed per page (GET /api/audit). */
export const AUDIT_PAGE_SIZE = 50;
/** A username kept on an audit row: what an anonymous request typed is cut here. */
export const AUDIT_USERNAME_MAX_LENGTH = 64;
/** A before/after value longer than this is cut with an ellipsis. */
export const HISTORY_VALUE_MAX_LENGTH = 120;

// --- Topology views -------------------------------------------------------

/** Rooms one saved view may name; more than this is the whole client anyway. */
export const TOPOLOGY_VIEW_MAX_ROOMS = 200;
/**
 * Nodes one saved view may pin to a place of their own (ROADMAP H2). A map
 * with more drawn things than this is read by scoping it, not by arranging
 * every node by hand.
 */
export const TOPOLOGY_VIEW_MAX_POSITIONS = 500;
/**
 * How far from the map's top-left corner a node may be dragged, in the
 * drawing units shared/topology.ts lays out in. Large enough for any map the
 * automatic layout produces, small enough that a stored position cannot blow
 * up the sheet a report prints.
 */
export const TOPOLOGY_POSITION_MAX = 20_000;

// --- Rack elevation ------------------------------------------------------

/** Heights a rack usually comes in, offered as choices (any other is typed). */
export const RACK_UNIT_CHOICES = [6, 9, 12, 15, 18, 22, 24, 27, 36, 42, 45, 48] as const;
export const RACK_MAX_UNITS = 100;
/** Standard rack/enclosure depths in inches, offered as a dropdown. */
export const RACK_DEPTH_CHOICES = ['12 in', '15 in', '18 in', '24 in', '30 in', '36 in'] as const;
/** How a rack is mounted; the map and report show it beside the size. */
export const RACK_MOUNT_TYPES = ['Wall', 'Floor-standing', 'Ceiling'] as const;
/**
 * A rack's form factor (ROADMAP A12). Stored as the code; '' means not set,
 * the way an unset depth or mount is ''. RACK_KIND_LABELS reads each aloud.
 */
export const RACK_KINDS = ['wall_mount', 'open_frame', 'two_post', 'cabinet'] as const;
export const RACK_KIND_LABELS: Readonly<Record<(typeof RACK_KINDS)[number], string>> = {
  wall_mount: 'Wall-mount',
  open_frame: 'Open frame',
  two_post: 'Two-post',
  cabinet: 'Cabinet',
};
/** A rack's role or purpose (MDF, IDF, AV…), free text; empty when not set. */
export const RACK_ROLE_MAX_LENGTH = 60;
/** A rack reservation's label (its purpose) and who it is for (ROADMAP A11). */
export const RESERVATION_LABEL_MAX_LENGTH = 80;
export const RESERVATION_OWNER_MAX_LENGTH = 80;
/**
 * The step a mounted device's height moves in (ROADMAP A12): half a rack
 * unit, so half-height gear (a 0.5U shelf, a 1.5U device) sits on a half-unit
 * boundary. It is also the smallest height a mounted device may have.
 */
export const RACK_UNIT_STEP = 0.5;
/** Heights a mounted device usually has; half steps for the small sizes (A12). */
export const ITEM_UNIT_CHOICES = [0.5, 1, 1.5, 2, 2.5, 3, 4, 5, 6, 8, 10] as const;
export const ITEM_MAX_UNITS = 50;
/** Past this many rows the U scale beside an elevation prints every other number. */
export const RACK_SCALE_DENSE_ROWS = 24;
/** Units between printed numbers on a tall rack's U scale. */
export const RACK_SCALE_TALL_STEP = 2;
/** Sizes a patch panel usually comes in, offered as one-tap picks. */
export const PATCH_PANEL_PORT_PRESETS = [8, 12, 16, 24, 48, 96] as const;

// --- Photos ---------------------------------------------------------------

export const BYTES_PER_KIBIBYTE = 1024;
export const BYTES_PER_MEBIBYTE = BYTES_PER_KIBIBYTE * BYTES_PER_KIBIBYTE;
/** Server-side ceiling for one uploaded image; the browser resizes well below it. */
export const PHOTO_MAX_BYTES = 10 * BYTES_PER_MEBIBYTE;
/** Thumbnails are small by construction; anything larger is not one. */
export const PHOTO_THUMB_MAX_BYTES = BYTES_PER_MEBIBYTE / 2;
/** Multipart framing and the caption, on top of the two image parts. */
export const PHOTO_UPLOAD_OVERHEAD_BYTES = BYTES_PER_MEBIBYTE / 16;
export const PHOTO_CAPTION_MAX_LENGTH = 200;
/** Longest edge of the stored image after the browser-side resize, in pixels. */
export const PHOTO_MAX_EDGE_PX = 2048;
/** Longest edge of the gallery thumbnail, in pixels. */
export const PHOTO_THUMB_EDGE_PX = 320;
export const PHOTO_JPEG_QUALITY = 0.85;
/** Formats the Worker accepts by their magic bytes; the browser always sends JPEG. */
export const PHOTO_CONTENT_TYPES = ['image/jpeg', 'image/png', 'image/webp'] as const;

// --- Reports --------------------------------------------------------------

/** Thumbnails embedded per room, rack, or item; the rest stay in the app. */
export const REPORT_PHOTOS_PER_NODE = 6;

// --- Time -----------------------------------------------------------------

export const MILLISECONDS_PER_SECOND = 1000;
export const SECONDS_PER_DAY = 24 * 60 * 60;

// --- Data export ----------------------------------------------------------

/** Bumped when the shape of GET /api/export changes incompatibly. */
// 2: the six `notes` columns became the `notes` table (migration 0026).
export const EXPORT_FORMAT_VERSION = 2;
/** The download is `<prefix>-<YYYY-MM-DD>.json`, dated the day it was made. */
export const EXPORT_FILE_NAME_PREFIX = 'project-map-export';

// --- Photo archive --------------------------------------------------------

/** Why a photo was archived: a sentence or two, never blank. */
export const PHOTO_ARCHIVE_REASON_MAX_LENGTH = 500;

// --- Photo tags and annotation --------------------------------------------

/**
 * How much of a photo's ISO taken-at instant is shown: "2026-09-03T14:12",
 * rendered as "2026-09-03 14:12". EXIF DateTimeOriginal is the camera's own
 * wall clock with no time zone, so the app keeps that reading verbatim (as
 * UTC) and shows it back unchanged rather than shifting it into a zone the
 * camera never claimed. Seconds are noise on a site record.
 */
export const TAKEN_AT_DISPLAY_LENGTH = 16;

/**
 * How much of a picked file the EXIF reader looks at for the date
 * (src/photos/exif.ts). A JPEG segment is at most 65535 bytes and APP1
 * comes before the image data, so the block is always near the front; a
 * quarter of a megabyte leaves room for a thumbnail or an XMP block ahead
 * of it without pulling a 12-megapixel file into memory twice.
 */
export const EXIF_SCAN_MAX_BYTES = 256 * 1024;

/** Tags one photo may carry; past a handful a label stops being a label. */
export const PHOTO_TAGS_MAX = 8;
/** One tag: a word or two, never a sentence. */
export const PHOTO_TAG_MAX_LENGTH = 30;
/**
 * Offered as one-tap picks beside the tag field. Any other short label can
 * be typed; these are the ones a site visit uses over and over.
 */
export const PHOTO_TAG_SUGGESTIONS = [
  'before',
  'after',
  'rack front',
  'rack rear',
  'wall plate',
  'label',
  'damage',
] as const;
/**
 * The archive reason written on the original when an annotated copy takes
 * its place. Annotation never overwrites the picture that was uploaded: the
 * marked-up copy is a new photo and the original goes to the archive, where
 * it can still be read and restored.
 */
export const PHOTO_ANNOTATED_REASON = 'Annotated copy replaces it';

// --- Notes log ------------------------------------------------------------

/** One note's body; the same ceiling the old free-text field had, so an imported note fits. */
export const NOTE_MAX_LENGTH = 5000;

// --- Share links ----------------------------------------------------------

// A share link is a signed-out way into the HTML report of ONE property
// (migration 0036, worker/routes/shares.ts). The secret lives only in the
// URL the manager copies once; the database keeps a SHA-256 of it, exactly
// as password resets do.

/** The longest a share link may live, and the ceiling the create route enforces. */
export const SHARE_LINK_MAX_DAYS = 30;
/** The shortest a share link may live: a link that expires today is still a day. */
export const SHARE_LINK_MIN_DAYS = 1;
/** One day in seconds, the unit a share link's life is asked for in. */
export const SHARE_DAY_SECONDS = 24 * 60 * 60;
/** Random bytes behind a share secret: 256 bits, base64url, like a reset token. */
export const SHARE_SECRET_BYTES = 32;
/** Characters of the secret kept in the clear, so a link can be named in lists and audit rows. */
export const SHARE_PREFIX_LENGTH = 8;
/** What a link is for, in the manager's words ("For the landlord"). */
export const SHARE_LABEL_MAX_LENGTH = 120;
/** Lifetimes offered as one-tap picks in the new-link form; any day up to the maximum is typeable. */
export const SHARE_LINK_DAY_PRESETS = [1, 7, 14, 30] as const;
/**
 * A secret is 43 base64url characters; anything longer than this is not one
 * and is refused before the database is touched.
 */
export const SHARE_SECRET_MAX_LENGTH = 64;
/** Where a share link is opened. Deliberately not under /api: it is anonymous. */
export const SHARE_PATH_PREFIX = '/share';
/** How the anonymous share limits are counted: one hour per source address. */
export const SHARE_RATE_LIMIT_WINDOW_SECONDS = 60 * 60;
/** Shared reports one address may open in the window; a client reading a report, not a scraper. */
export const SHARE_VIEWS_PER_HOUR = 60;
/** Unknown, expired, or revoked secrets one address may present in the window. */
export const SHARE_MAX_FAILURES_PER_IP = 20;
/** What an anonymous share view is called in the audit log, where there is no account. */
export const SHARE_AUDIT_ACTOR = 'a visitor';

// --- Device import (src/import, worker/routes/import.ts) -------------------

/** Rows one import may carry; a bigger export is split by hand. */
export const IMPORT_MAX_ROWS = 500;
/** Rows the mapping step shows before offering the rest. */
export const IMPORT_PREVIEW_ROWS = 10;
// What a device cost (ROADMAP J1). It is stored in whole cents so a sum of
// costs cannot drift the way a float would, and turned back into units of the
// account's own currency only where it is typed and shown.
export const CENTS_PER_UNIT = 100;
export const CURRENCY_DECIMALS = 2;

/** Hex digits in a MAC address (six octets). */
export const MAC_HEX_DIGITS = 12;
/** Hex digits in an OUI, the maker's own prefix: the first three octets (ROADMAP J9). */
export const OUI_HEX_DIGITS = 6;
/** Octets in an IPv4 address and the highest value one can hold. */
export const IPV4_OCTETS = 4;
export const IPV4_OCTET_MAX = 255;
/** Groups in an IPv6 address written in full, and the hex digits one may hold. */
export const IPV6_GROUPS = 8;
export const IPV6_GROUP_MAX_DIGITS = 4;
/** The longest prefix a CIDR may declare, per family ("10.0.10.0/24", "2001:db8::/64"). */
export const IPV4_PREFIX_MAX = 32;
export const IPV6_PREFIX_MAX = 128;

// --- Checklists -----------------------------------------------------------

/** A checklist's name ("Commissioning", "Handover 2026"). */
export const CHECKLIST_NAME_MAX_LENGTH = 120;
/** One step of a checklist: a sentence, not a procedure. */
export const CHECKLIST_ITEM_MAX_LENGTH = 500;
/** What was found or done at a step, written beside the tick. */
export const CHECKLIST_ITEM_NOTE_MAX_LENGTH = 1000;

// --- Work orders (migration 0062, ROADMAP N5) ------------------------------

/** A work order's title ("Fit the second-floor switches"). */
export const WORK_ORDER_TITLE_MAX_LENGTH = 120;
/** Who is doing the work, as typed: a technician, a crew, a contractor. */
export const WORK_ORDER_ASSIGNEE_MAX_LENGTH = 120;
/** What the order says about itself beyond its lines. */
export const WORK_ORDER_NOTES_MAX_LENGTH = 2000;
/** The note beside one line ("use the spare in the van"). */
export const WORK_ORDER_LINE_NOTE_MAX_LENGTH = 500;

// --- Report branding and sections (migration 0064, ROADMAP N2) ---------------

/** The company name on every report ("Acme Networks"); matches the SQL CHECK. */
export const REPORT_COMPANY_MAX_LENGTH = 120;
/** The line under the report's title ("Prepared for …"). */
export const REPORT_COVER_MAX_LENGTH = 500;
/** The line at the foot of every report page. */
export const REPORT_FOOTER_MAX_LENGTH = 300;
/** A logo the browser has already resized: half a mebibyte is ample for a header mark. */
export const REPORT_LOGO_MAX_BYTES = 512 * 1024;

/**
 * A preset's product picture (ROADMAP H5). Drawn at icon size on the map, the
 * rack face, and the lists, and the map carries every drawn model's picture at
 * once, so the same half-mebibyte ceiling the logo has is generous here.
 */
export const PRESET_PICTURE_MAX_BYTES = 512 * 1024;

// --- Service requests (migration 0063, ROADMAP M2) -------------------------

/** What a client asks for, in a line ("Wi-Fi drops in the lobby"). */
export const SERVICE_REQUEST_TITLE_MAX_LENGTH = 120;
/** What is wrong, where, since when. */
export const SERVICE_REQUEST_BODY_MAX_LENGTH = 2000;
/** The team's answer under the request. */
export const SERVICE_REQUEST_RESPONSE_MAX_LENGTH = 2000;

// --- Device model presets (migration 0034) --------------------------------

/** A preset's name, as the item form's picker lists it ("UniFi USW-24-PoE"). */
export const PRESET_NAME_MAX_LENGTH = 120;
/** Presets one library file may bring in at once (ROADMAP O2); a library, not a bulk load. */
export const PRESET_IMPORT_MAX = 200;
/** The file name the preset library downloads as. */
export const PRESET_LIBRARY_FILENAME = 'project-map-presets.json';

// --- Custom asset types (migration 0052, ROADMAP B8) ----------------------

/** A custom type's name, shown in the type picker beside the built-in ones. */
export const CUSTOM_TYPE_NAME_MAX_LENGTH = 40;
/** A custom field's stable key ("assetTag"): letters, digits, and underscores. */
export const CUSTOM_FIELD_KEY_MAX_LENGTH = 40;
/** A custom field's or type's human label ("Asset tag"). */
export const CUSTOM_FIELD_LABEL_MAX_LENGTH = 60;
/** Fields one custom type may define; enough for a real record, not a database. */
export const CUSTOM_TYPE_MAX_FIELDS = 40;
/** Options a custom choice field may list. */
export const CUSTOM_FIELD_MAX_OPTIONS = 40;
/** One custom choice option's text. */
export const CUSTOM_FIELD_OPTION_MAX_LENGTH = 60;
/** The typed kinds a custom field may be, matching the form builder (ROADMAP B8). */
export const CUSTOM_FIELD_KINDS = ['text', 'number', 'choice', 'boolean', 'url', 'date'] as const;
/** A stored custom value's text ceiling (a URL, a date, a free string). */
export const CUSTOM_VALUE_MAX_LENGTH = 1000;
/** The keys `details` reserves for the selected custom type and its validated values (ROADMAP B8). */
export const CUSTOM_TYPE_DETAIL_KEY = 'customType';
export const CUSTOM_VALUES_DETAIL_KEY = 'custom';

// --- Contacts and site access ---------------------------------------------

/** A contact's name: a person or a desk ("Front desk"), not a paragraph. */
export const CONTACT_NAME_MAX_LENGTH = 120;
/** What the contact is to the site ("Office manager", "Landlord"). */
export const CONTACT_ROLE_MAX_LENGTH = 100;
/** An alarm, gate, or lockbox code as typed. */
export const ACCESS_CODE_MAX_LENGTH = 100;
/** The free note beside the codes: which panel, which keypad, where the key is. */
export const ACCESS_NOTES_MAX_LENGTH = 2000;

// --- Attachments (files on a node) ----------------------------------------

/** Ceiling for one uploaded file; with the multipart framing it must fit under FORM_BODY_MAX_BYTES. */
export const ATTACHMENT_MAX_BYTES = 10 * BYTES_PER_MEBIBYTE;
/** Multipart framing, the kind, and the title, on top of the file part. */
export const ATTACHMENT_UPLOAD_OVERHEAD_BYTES = BYTES_PER_MEBIBYTE / 16;
/** An optional title shown instead of the file name. */
export const ATTACHMENT_TITLE_MAX_LENGTH = 120;
/** The name the file was uploaded with, as most file systems bound it. */
export const ATTACHMENT_FILENAME_MAX_LENGTH = 255;
/**
 * What the Worker stores a file as. PDF and images are told by their magic
 * bytes; the text types by an accepted extension and valid UTF-8; the
 * tester and config exports by extension alone, served as bytes.
 */
export const ATTACHMENT_CONTENT_TYPES = [
  'application/pdf',
  'image/jpeg',
  'image/png',
  'image/webp',
  'text/plain',
  'text/csv',
  'application/json',
  'application/octet-stream',
  // Video and audio memos (ROADMAP L5), each told by its own magic bytes in
  // worker/photos/sniff.ts. The two lists below are the same values, kept
  // apart so a kind can be checked against the family its bytes turned out to
  // be; this list is what the column may hold.
  'video/mp4',
  'video/quicktime',
  'video/webm',
  'audio/mpeg',
  'audio/mp4',
  'audio/wav',
] as const;
/** Extensions of the types told by their signature, as the file picker offers them. */
export const ATTACHMENT_SIGNATURE_EXTENSIONS = ['pdf', 'jpg', 'jpeg', 'png', 'webp'] as const;
/** Extensions accepted without a magic-byte signature (lower-case, no dot). */
export const ATTACHMENT_TEXT_EXTENSIONS = ['txt', 'log', 'csv', 'json'] as const;
/** Tester and config exports (LinkWare `.flw`, device `.cfg`/`.conf`): stored and served as bytes. */
export const ATTACHMENT_BINARY_EXTENSIONS = ['flw', 'cfg', 'conf'] as const;
/** The PDF signature; a `.pdf` without it is refused. */
export const PDF_SIGNATURE = '%PDF';
/** Content types that start this way are pictures a browser can draw in an `<img>`. */
export const IMAGE_CONTENT_TYPE_PREFIX = 'image/';

// --- Floor plans (a plan image with device pins) --------------------------

/** How far one arrow-key press moves the focused pin, as a fraction of the image. */
export const PIN_NUDGE = 0.01;
/** The report prints a plan of at most this many mebibytes. */
const REPORT_PLAN_MEBIBYTES = 1;
/**
 * A plan image the report embeds as a data URI. Attachments keep no
 * thumbnail of their own, so a plan larger than this is named in the report
 * instead of printed; the document (and the PDF renderer's memory) stays
 * bounded whatever was uploaded.
 */
export const REPORT_PLAN_MAX_BYTES = REPORT_PLAN_MEBIBYTES * BYTES_PER_MEBIBYTE;

// --- Floor-plan drawing surface (floors and shapes, migration 0044) --------

// A floor is drawn in its own coordinate space: a viewBox of PLAN_UNITS_WIDE
// by PLAN_UNITS_HIGH abstract units. Every shape stores its points in that
// space, never in pixels, so the same floor reads on a phone column, a
// desktop panel, and a printed page — the way a plan pin is a fraction of its
// image. Lengths are those units multiplied by the floor's scale, once one is
// set by measuring a known distance (D2).

/** The width of a floor's drawing space in abstract plan units. */
export const PLAN_UNITS_WIDE = 1600;
/** The height of a floor's drawing space in abstract plan units (a 4:3 sheet). */
export const PLAN_UNITS_HIGH = 1200;
/** A new line or wall vertex within this many units of an existing endpoint snaps to it. */
export const PLAN_SNAP_UNITS = 24;
/** A drawn segment within this many degrees of a right angle snaps square. */
export const PLAN_SNAP_ANGLE_DEGREES = 8;
/** One arrow-key nudge of a selected shape, in plan units. */
export const PLAN_NUDGE_UNITS = 8;
/** The scale bar aims for about this fraction of the canvas width, rounded to a tidy length. */
export const PLAN_SCALE_BAR_TARGET = 0.25;
/** A floor name is a short label; bound it the way a room's is. */
export const FLOOR_NAME_MAX_LENGTH = 60;
/** A shape's text note (a markup label) is bounded so a stray paste cannot fill the row. */
export const PLAN_SHAPE_LABEL_MAX_LENGTH = 120;
/** A single shape may hold at most this many points, so one polyline cannot be unbounded. */
export const PLAN_SHAPE_MAX_POINTS = 200;

// --- Offline editing and sync (migration 0040) -----------------------------

// A technician works in plant rooms, basements, and lifts with no signal. The
// app keeps a copy of the properties it was told to keep, answers reads from
// it when the network is gone, and queues writes until it is back. Three
// pieces of the protocol live on both sides of the wire, so they are named
// here rather than twice.

/**
 * The header a write carries so replaying it is safe: a UUID the client
 * generates once per operation. The Worker atomically claims the write and
 * replays a matching completed reply within the retention window. An
 * uncertain outcome refuses another execution while its claim remains.
 */
export const IDEMPOTENCY_KEY_HEADER = 'Idempotency-Key';
/**
 * The header a write carries to say which version of the row it was made
 * against: the `updatedAt` the client last saw, quoted as an ETag. A single
 * node's GET returns the same value in `ETag`.
 */
export const IF_MATCH_HEADER = 'If-Match';
/** Completed-reply retention; uncertain claims never expire automatically. */
export const IDEMPOTENCY_TTL_HOURS = 48;
export const IDEMPOTENCY_TTL_SECONDS = IDEMPOTENCY_TTL_HOURS * 60 * 60;
/**
 * The largest reply kept for replay. Every write's reply is one node or a
 * short list. A larger reply is answered normally but leaves an uncertain
 * claim: a retry cannot run the route again and duplicate the write.
 */
export const IDEMPOTENCY_RESPONSE_MAX_BYTES = 64 * BYTES_PER_KIBIBYTE;
/** Rows one manifest answers with before it says `truncated`. */
export const SYNC_MANIFEST_LIMIT = 2000;
/** Photos and files one offline bundle names, newest first, before it says `truncated`. */
export const OFFLINE_BUNDLE_MEDIA_LIMIT = 500;

// --- Knowledge base: articles and runbooks (migration 0042) ----------------

// An article is a documentation page for a client or one of its properties:
// a site summary, a reboot order, an ISP-outage runbook. The body is stored
// as portable Markdown text (never HTML), so the stored value is inert and is
// escaped on render exactly as the report builder escapes everything; the
// renderer only ever emits the fixed set of tags its tokens name (see
// shared/article-body.ts). The choice and its rationale are in PLAN.md §5.

/** An article's title ("Reboot order", "ISP outage steps"). */
export const ARTICLE_TITLE_MAX_LENGTH = 200;
/** An article's Markdown body; a long runbook fits, and it stays well under JSON_BODY_MAX_BYTES. */
export const ARTICLE_BODY_MAX_LENGTH = 50_000;
/** The key of the template an article was started from ('' for a blank one). */
export const ARTICLE_TEMPLATE_MAX_LENGTH = 40;
/** Articles listed for one node before the list says how many more there are. */
export const ARTICLE_LIST_LIMIT = 200;
/** How much of a body a search hit or a list preview shows. */
export const ARTICLE_EXCERPT_MAX_LENGTH = 160;

// --- Expiring records: SSL certificates, domains, licences (migration 0042) -

/** An expiring record's name ("acme.example.com", "Windows Server 2022"). */
export const RECORD_NAME_MAX_LENGTH = 200;
/** The record's own identifier (a serial, a certificate fingerprint, an order number). */
export const RECORD_IDENTIFIER_MAX_LENGTH = 200;
/** The renewal note beside the record: who renews it, where, and how. */
export const RECORD_NOTES_MAX_LENGTH = 2000;
/** Records listed for one client before the list says how many more there are. */
export const RECORD_LIST_LIMIT = 200;

// --- Config snapshots and diff (migration 0042, shared/diff.ts) ------------

/**
 * The largest config attachment the diff view reads into the browser. A
 * device configuration is text; anything past this is not one, and reading it
 * to diff it line by line would be a memory cost the phone should not pay.
 */
export const CONFIG_DIFF_MAX_BYTES = 2 * BYTES_PER_MEBIBYTE;
/** Lines one side of a config diff reads before it stops, so the browser stays responsive. */
export const CONFIG_DIFF_MAX_LINES = 20_000;
/**
 * The largest LCS table the diff builds (rows × columns of the changed middle,
 * after the common prefix and suffix are trimmed). Beyond it the middle is
 * diffed coarsely rather than allocating a table that would exhaust memory.
 */
export const CONFIG_DIFF_MAX_CELLS = 4_000_000;

// --- Resources: the estate-wide tables (shared/schemas/resources.ts) --------

// One flat, filterable table per record type across every client. The Worker
// fetches the rows a scope allows, filters and sorts them, and answers one
// page at a time; a CSV export walks the whole matching set at once.

/** Rows a resource page returns by default. */
export const RESOURCE_PAGE_SIZE = 50;
/** The largest page a request may ask for, so one call cannot pull the estate. */
export const RESOURCE_PAGE_SIZE_MAX = 200;
/** Rows a CSV export of "all matching" writes at most, whatever the estate holds. */
export const RESOURCE_EXPORT_MAX = 5000;

// --- Cross-node records: tags, relationships, responsibility (migration 0053)

/** A tag's label ("critical", "guest network", "to replace"). */
export const TAG_NAME_MAX_LENGTH = 60;
/** The note beside a typed relationship ("fails over to", "documented in run book"). */
export const RELATIONSHIP_NOTE_MAX_LENGTH = 500;
/** Relationship candidates returned before the caller must narrow its search. */
export const CROSS_NODE_LIST_LIMIT = 200;

// --- Networks: VLANs, WLANs, IP space, circuits (migrations 0057–0059) ------

// The network records a client's map needs beside its devices (ROADMAP G1–G7).
// A VLAN is defined once for the client and may be narrowed to one property;
// a WLAN is the wireless side of one; an IP reservation is an address someone
// set aside inside a VLAN's subnet; a circuit is the ISP service a WAN link
// lands on. Documentation, never management: nothing here is pushed to a
// device and nothing is scanned.

/** The paragraph beside a VLAN's one-line purpose. */
export const VLAN_NOTES_MAX_LENGTH = 2000;
/** VLANs listed for one client before the list says how many more there are. */
export const VLAN_LIST_LIMIT = 500;

/** An SSID is at most 32 octets on the air; the record keeps the same ceiling. */
export const WLAN_SSID_MAX_LENGTH = 32;
/** Which bands a WLAN is broadcast on; '' while nobody has recorded one. */
export const WLAN_BANDS = ['2.4 GHz', '5 GHz', '6 GHz', '2.4 + 5 GHz', 'All bands'] as const;
/** How a WLAN is secured, as a controller names it. */
export const WLAN_SECURITY_MODES = [
  'Open',
  'WPA2 Personal',
  'WPA2 Enterprise',
  'WPA3 Personal',
  'WPA3 Enterprise',
  'WPA2/WPA3',
  'WEP',
] as const;
/** The note beside a WLAN (what joins it, which controller owns it). */
export const WLAN_NOTES_MAX_LENGTH = 2000;
/** WLANs one client may hold; a site with more than this is not a Wi-Fi record. */
export const WLAN_LIST_LIMIT = 200;
/** WLANs one access point may broadcast at once. */
export const WLAN_BROADCAST_MAX = 32;

/** An address cell: an IPv6 address with a zone is the longest thing written here. */
export const IP_ADDRESS_MAX_LENGTH = 60;
/** What a reservation is for ("printer, static by the vendor", "gateway"). */
export const IP_RESERVATION_PURPOSE_MAX_LENGTH = 200;
/** The note beside a reservation. */
export const IP_RESERVATION_NOTES_MAX_LENGTH = 2000;
/** Reservations one subnet may hold. */
export const IP_RESERVATION_LIST_LIMIT = 500;
/**
 * Addresses one subnet's list names before it says how many more are in use.
 * A /16 is counted, never listed: the page reads the addresses that are
 * actually recorded, not every address the block could hold.
 */
export const IP_ADDRESS_LIST_LIMIT = 500;
/** Segments the free-space bar is drawn in, so a /24 and a /64 both read at a glance. */
export const IP_FREE_BAR_SEGMENTS = 40;

/** An ISP's name on a circuit record. */
export const CIRCUIT_PROVIDER_MAX_LENGTH = 120;
/** The provider's own reference for the circuit ("ACME-12345-678"). */
export const CIRCUIT_REFERENCE_MAX_LENGTH = 120;
/** Where the circuit lands at each end ("MDF, 2nd floor", "Provider POP, Elm St"). */
export const CIRCUIT_END_MAX_LENGTH = 200;
/** The note beside a circuit. */
export const CIRCUIT_NOTES_MAX_LENGTH = 2000;
/** How the circuit hands off to the customer's own equipment. */
export const CIRCUIT_HANDOFFS = [
  'RJ45 copper',
  'Single-mode fiber',
  'Multi-mode fiber',
  'Coax',
  'DSL',
  'Fixed wireless',
  'Other',
] as const;
/**
 * The largest bandwidth a circuit records, in Mbps: 400 Gbps, far past any
 * service a low-voltage integrator installs, so a typo is caught and a real
 * figure never is.
 */
export const CIRCUIT_BANDWIDTH_MAX_MBPS = 400_000;
/** Circuits one client may hold. */
export const CIRCUIT_LIST_LIMIT = 200;
/** Megabits in a gigabit, so a bandwidth reads as "1 Gbps" rather than "1000 Mbps". */
export const MBPS_PER_GBPS = 1000;

// --- W12 L1: where a photo was taken (migration 0060) ---------------------

// The browser is asked for a position once per session, at the moment photos
// are picked or the camera is used on a plan pin. It is never waited on: the
// ask has a hard timeout, and a refusal, a missing API, or a failed fix all
// answer "no point" so the upload goes on without one.

/** How long the browser is given to report a position before the photo goes up without one. */
export const PHOTO_LOCATION_TIMEOUT_MS = 8000;
/** A fix this recent is still "where I am standing", so the phone need not wake its radio again. */
export const PHOTO_LOCATION_MAX_AGE_MS = 60_000;
/** Decimal places a coordinate is written to: five is about a metre at the equator. */
export const COORDINATE_DISPLAY_DIGITS = 5;
/** The range a latitude lives in; the same bound the column CHECK carries. */
export const LATITUDE_MAX_DEGREES = 90;
/** The range a longitude lives in; the same bound the column CHECK carries. */
export const LONGITUDE_MAX_DEGREES = 180;
/**
 * Placed photos drawn on one property's map picture. A static map marker's
 * label is a single character, so the numbered pins stop at nine and the page
 * says how many more photos carried a position.
 */
export const MAP_PHOTO_MAX_PINS = 9;

// --- W12 L2: the property photo timeline ----------------------------------

/** Photos one property's timeline answers with; past this the page says how many were left out. */
export const PHOTO_TIMELINE_LIMIT = 500;
/** How much of an ISO instant is the calendar day the timeline groups by: `YYYY-MM-DD`. */
export const PHOTO_TIMELINE_DAY_LENGTH = 10;

// --- W12 L4: the shared photo gallery -------------------------------------

/**
 * Photos one shared gallery embeds. Every thumbnail travels in the document as
 * a data URI, because the share route serves one self-contained page and can
 * fetch nothing (the report's own `default-src 'none'` policy), so the cap is
 * what keeps that page printable and the Worker's time bounded. Past it the
 * page says how many more the property holds rather than trailing off.
 */
export const GALLERY_PHOTO_MAX = 120;

// --- W12 L5: video and audio memos (migration 0061) -----------------------

// A memo is an attachment like any other file — archived with a reason, named
// in the report, found in the export — but it is bigger than a manual and it
// plays rather than downloads, so it carries its own ceiling and its own
// signature check. The caps are per kind: a minute of phone video is worth far
// more bytes than a spoken note.

/** Ceiling for one uploaded video memo; roughly a minute of phone video. */
export const VIDEO_MAX_BYTES = 25 * BYTES_PER_MEBIBYTE;
/** Ceiling for one uploaded audio memo; roughly eight minutes at 128 kbps. */
export const AUDIO_MAX_BYTES = 8 * BYTES_PER_MEBIBYTE;
/** What the Worker stores a video memo as, each told by its magic bytes. */
export const VIDEO_CONTENT_TYPES = ['video/mp4', 'video/quicktime', 'video/webm'] as const;
/** What the Worker stores an audio memo as, each told by its magic bytes. */
export const AUDIO_CONTENT_TYPES = ['audio/mpeg', 'audio/mp4', 'audio/wav'] as const;
/** Extensions of the video types, as the file picker offers them (lower-case, no dot). */
export const ATTACHMENT_VIDEO_EXTENSIONS = ['mp4', 'm4v', 'mov', 'webm'] as const;
/** Extensions of the audio types, as the file picker offers them (lower-case, no dot). */
export const ATTACHMENT_AUDIO_EXTENSIONS = ['mp3', 'm4a', 'wav'] as const;
/**
 * The longest run time a memo may declare, in seconds. The browser reads the
 * duration off the file before it uploads it; anything past four hours is a
 * misread header rather than a note, and is filed as "unknown" instead.
 */
export const MEDIA_DURATION_MAX_SECONDS = 4 * 60 * 60;
/** Seconds in a minute, for writing a memo's run time as "4:07". */
export const SECONDS_PER_MINUTE = 60;
/** How long a deleted node stays in the recycle bin before it is purged (ROADMAP M3). */
export const RECYCLE_BIN_RETENTION_DAYS = 30;
export const RECYCLE_BIN_RETENTION_SECONDS = RECYCLE_BIN_RETENTION_DAYS * 24 * 60 * 60;

// --- W11 D8: plan markups (migration 0067) --------------------------------

// A markup says something about the plan rather than recording what is on it:
// a freehand line, an arrow that points, a revision cloud round what changed.
// All three are drawn in the floor's own abstract unit space, so these sizes
// are plan units and read the same on a phone, a desktop, and a printed page.

/**
 * The length one scallop of a revision cloud spans along the outline. Each
 * edge is cut into whole scallops of about this size, so a small cloud still
 * gets at least one bulge per edge and a large one is not a row of pinpricks.
 */
export const PLAN_CLOUD_ARC_UNITS = 48;
/**
 * A scallop's radius as a multiple of the chord it spans. It must be over a
 * half for the arc to exist at all; above that it is how far the bulge stands
 * out from the outline.
 */
export const PLAN_CLOUD_BULGE = 0.62;
/** How long an arrow's barbs are, in plan units. */
export const PLAN_ARROW_HEAD_UNITS = 34;
/** The angle each barb makes with the shaft, in degrees. */
export const PLAN_ARROW_HEAD_DEGREES = 26;

// --- W11 D9: plan revisions (migration 0068) ------------------------------

/**
 * How far back a plan's revision chain is followed when the older revisions
 * are listed. A plan that has been reissued this many times is a plan whose
 * history is a document of its own; the list says it stops rather than walking
 * an unbounded chain on every read.
 */
export const PLAN_REVISION_MAX = 50;

// --- W11 D4: a run's length read off the plan -----------------------------

/**
 * The divisor an estimated cable length is rounded by: ten, so the number
 * offered reads to one decimal, the way a length field is typed. The estimate
 * is a straight line between two pinned devices and is offered for a person to
 * accept, never written on its own, so more precision would only look like a
 * measurement it is not.
 */
export const PLAN_ESTIMATE_DECIMALS = 10;

// --- W11 D11: capacity colouring ------------------------------------------

// One scale for every "how full is it" figure the map draws (shared/capacity.ts):
// a room's devices, a room's ports, a rack's units, a switch's PoE budget. The
// thresholds are fractions of the total, chosen so "filling up" starts where a
// technician would begin to plan the next panel rather than at an arbitrary
// half.

/** Anything used at all, up to this fraction of the total, reads as room to spare. */
export const CAPACITY_LIGHT_FRACTION = 0.01;
/** From this fraction of the total, a thing reads as filling up. */
export const CAPACITY_BUSY_FRACTION = 0.6;
/** From this fraction of the total, a thing reads as nearly full. */
export const CAPACITY_FULL_FRACTION = 0.85;
